import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import {
  FilterActions,
  FilterTrigger,
  useFilterDraft,
  type FilterTriggerProps,
} from './filter-popover'
import { Checkbox } from './checkbox'
import { SearchBar } from './search-bar'
import { cn } from '../../lib/utils'

/**
 * MultiSelectFilter — a searchable checkbox-list filter as a `Popover`: a pill
 * trigger opens a panel with a "Select all" header, a search box, a scrollable
 * checkbox list, and a Reset / Cancel / Apply footer. The trigger shows the
 * applied count; edits stage in a draft and only commit on Apply.
 *
 *   <MultiSelectFilter label="City" options={cities} onApply={setCities} />
 *
 * This is a *value picker for filtering*, not a typeahead token input — for the
 * latter use `MultiCombobox`. "Select all" acts on the currently-filtered rows,
 * so it composes with the search box.
 */

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectFilterProps {
  label: React.ReactNode
  options: MultiSelectOption[]
  /** Initial applied selection (uncontrolled). */
  defaultValue?: string[]
  onApply?: (value: string[]) => void
  /** Show the search box (default true). Pass false for short, fixed lists. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Trigger corner style — `rect` (default) or `pill`. */
  shape?: FilterTriggerProps['shape']
  /** Trigger height — `md` (h-9, default) or `sm` (h-8) for a compact toolbar. */
  size?: FilterTriggerProps['size']
  /** Trigger label placement — `border` (notched outline, default) or `inside`. */
  labelPlacement?: FilterTriggerProps['labelPlacement']
  /** Classes on the trigger (e.g. a width). */
  className?: string
  /** Props forwarded to the trigger pill. */
  triggerProps?: Omit<FilterTriggerProps, 'label' | 'summary' | 'active'>
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v))

export function MultiSelectFilter({
  label,
  options,
  defaultValue,
  onApply,
  searchable = true,
  searchPlaceholder = 'Search',
  shape,
  size,
  labelPlacement = 'border',
  className,
  triggerProps,
}: MultiSelectFilterProps) {
  const [committed, setCommitted] = React.useState<string[]>(defaultValue ?? [])
  const [query, setQuery] = React.useState('')
  const { open, onOpenChange, draft, setDraft, apply, cancel } = useFilterDraft<
    string[]
  >(committed, (next) => {
    setCommitted(next)
    onApply?.(next)
  })

  // Clear the search whenever the popover closes, so a stale query doesn't
  // persist into the next open (mirrors the open handler reseeding the draft).
  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery('')
    onOpenChange(next)
  }

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  const draftSet = React.useMemo(() => new Set(draft), [draft])
  const toggle = (value: string) =>
    setDraft((d) =>
      d.includes(value) ? d.filter((v) => v !== value) : [...d, value]
    )

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => draftSet.has(o.value))
  const someFilteredSelected = filtered.some((o) => draftSet.has(o.value))
  const toggleAll = () =>
    setDraft((d) => {
      const visible = filtered.map((o) => o.value)
      if (allFilteredSelected) return d.filter((v) => !visible.includes(v))
      return Array.from(new Set([...d, ...visible]))
    })

  // Clear the applied selection entirely (× in the trigger) — commits an empty
  // value and reseeds the draft so reopening starts clean.
  const clear = () => {
    setCommitted([])
    setDraft([])
    onApply?.([])
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="relative">
        <PopoverTrigger asChild>
          <FilterTrigger
            label={label}
            summary={
              committed.length === 0
                ? undefined
                : labelPlacement === 'border'
                  ? `${committed.length} selected`
                  : committed.length
            }
            active={committed.length > 0}
            shape={shape}
            size={size}
            labelPlacement={labelPlacement}
            trailingReserve={committed.length > 0}
            className={className}
            {...triggerProps}
          />
        </PopoverTrigger>
        {/* Inline clear — a sibling of the trigger (not nested in its <button>)
            so it stays valid + doesn't open the popover. Shown once applied. */}
        {committed.length > 0 && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={(e) => {
              e.stopPropagation()
              clear()
            }}
            className="absolute right-8 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded p-0.5 text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
          >
            <X weight="bold" aria-hidden="true" className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent align="start" className="w-64">
        {searchable && (
          <div className="mb-1.5">
            <SearchBar
              size="sm"
              value={query}
              onValueChange={setQuery}
              placeholder={searchPlaceholder}
              clearable
            />
          </div>
        )}
        <Checkbox
          size="sm"
          checked={allFilteredSelected}
          indeterminate={someFilteredSelected && !allFilteredSelected}
          onChange={toggleAll}
          label="Select all"
          wrapperClassName="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 font-medium text-fbui-gray-800 hover:bg-fbui-gray-100"
        />
        {/* Always separate Select all from the options. */}
        <div className="-mx-1 my-1.5 h-px bg-fbui-gray-200" />
        {/* Cap at 6 option rows (6 × 32px sm rows = 12rem) before scrolling. */}
        <div className="fbui-scroll max-h-48 overflow-y-auto">
          {filtered.length > 0 ? (
            filtered.map((o) => (
              <Checkbox
                key={o.value}
                size="sm"
                checked={draftSet.has(o.value)}
                onChange={() => toggle(o.value)}
                label={o.label}
                wrapperClassName="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-fbui-gray-700 hover:bg-fbui-gray-100"
              />
            ))
          ) : (
            <p className={cn('px-3 py-2 text-sm text-fbui-gray-500')}>
              No matches
            </p>
          )}
        </div>
        <FilterActions
          onReset={() => setDraft([])}
          onCancel={cancel}
          onApply={apply}
          resetDisabled={draft.length === 0}
          applyDisabled={sameSet(draft, committed)}
        />
      </PopoverContent>
    </Popover>
  )
}
